
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { FileUp, CheckCircle, AlertCircle } from "lucide-react";
import { importArticleRedirects } from "@/utils/importArticleRedirects";
import { useToast } from "@/hooks/use-toast";

const ArticleRedirectImporter: React.FC = () => {
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<{
    success: number;
    failed: number;
    message: string;
  } | null>(null);
  const { toast } = useToast();

  const handleImport = async () => {
    try {
      setIsImporting(true);
      setImportResult(null);
      
      const result = await importArticleRedirects();
      setImportResult(result);
      
      toast({
        title: "Import Complete",
        description: result.message,
        variant: result.failed > 0 ? "destructive" : "default",
      });
    } catch (error) {
      console.error('Import failed:', error);
      toast({
        title: "Import Failed",
        description: error instanceof Error ? error.message : "Failed to import redirects",
        variant: "destructive",
      });
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileUp className="h-5 w-5" />
          Article URL Redirects
        </CardTitle>
        <CardDescription>
          Import redirects for /article/ URLs to /blog/ URLs to fix Ahrefs 404 errors
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {importResult && (
          <Alert variant={importResult.failed > 0 ? "destructive" : "default"}>
            {importResult.failed > 0 ? (
              <AlertCircle className="h-4 w-4" />
            ) : (
              <CheckCircle className="h-4 w-4" />
            )}
            <AlertDescription>
              {importResult.message}
            </AlertDescription>
          </Alert>
        )}
        
        <div className="space-y-2">
          <p className="text-sm text-gray-600">
            This will create 301 redirects for all the /article/ URLs found in your Ahrefs report 
            to redirect them to /blog/ URLs. This will help resolve the 404 errors and preserve 
            any existing SEO value.
          </p>
          <p className="text-sm text-gray-600">
            Total redirects to import: <strong>35 article URLs</strong>
          </p>
        </div>
        
        <Button 
          onClick={handleImport} 
          disabled={isImporting}
          className="w-full"
        >
          {isImporting ? (
            <>
              <FileUp className="h-4 w-4 mr-2 animate-spin" />
              Importing Redirects...
            </>
          ) : (
            <>
              <FileUp className="h-4 w-4 mr-2" />
              Import Article Redirects
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default ArticleRedirectImporter;
